import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BroadcastIcon, TrashIcon, StopCircleIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "live", label: "Live" },
  { id: "scheduled", label: "Scheduled" },
  { id: "ended", label: "Ended" },
];

/**
 * Admin tool: list every stream on the platform and let the admin force-end
 * or permanently delete any one of them. Used for emergency takedowns
 * (abusive sellers, leaked PII in chat, stale demo streams, etc.).
 */
export default function AdminStreamsManager() {
  const [filter, setFilter] = useState("live");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // stream_id currently being acted on
  const [deleteTarget, setDeleteTarget] = useState(null); // stream object pending delete
  const [confirmText, setConfirmText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", filter);
      if (q.trim()) params.set("q", q.trim());
      const res = await api.get(`/admin/streams?${params.toString()}`);
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load streams"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const endStream = async (s) => {
    if (!s?.id) return;
    if (!window.confirm(`End "${s.title || s.id}" right now? Viewers will be ejected.`)) return;
    setBusy(s.id);
    try {
      await api.post(`/admin/streams/${s.id}/end`);
      toast.success("Stream ended.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't end stream"));
    } finally {
      setBusy(null);
    }
  };

  const askDelete = (s) => {
    setDeleteTarget(s);
    setConfirmText("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm.');
      return;
    }
    setBusy(deleteTarget.id);
    try {
      const res = await api.delete(`/admin/streams/${deleteTarget.id}`);
      toast.success(
        `Stream removed. Cleared ${res.data?.summary?.chat_messages || 0} chat messages.`
      );
      setDeleteTarget(null);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(apiError(e, "Couldn't delete stream"));
    } finally {
      setBusy(null);
    }
  };

  const liveCount = useMemo(
    () => items.filter((s) => s.status === "live").length,
    [items]
  );

  return (
    <div className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden" data-testid="admin-streams-section">
      <div className="p-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display font-bold flex items-center gap-2">
            <BroadcastIcon size={18} className="text-[#1D78AB]" />
            Live show controls
            <span className="text-xs font-normal text-zinc-500">
              ({items.length} {filter === "all" ? "total" : filter})
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 font-mono">
            Force-end or permanently delete any stream on the platform.
            {liveCount > 0 && filter === "all" && (
              <span className="ml-2 text-emerald-600">{liveCount} live now</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={
                "px-3 py-1.5 text-xs font-bold rounded-full transition-colors " +
                (filter === f.id
                  ? "bg-[#1D78AB] text-white"
                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200")
              }
              data-testid={`admin-streams-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="admin-streams-refresh"
            className="ml-1"
          >
            <ArrowsClockwiseIcon size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <form onSubmit={onSearch} className="p-3 border-b border-zinc-200 flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, seller name, or stream ID…"
          className="flex-1"
          data-testid="admin-streams-search-input"
        />
        <Button type="submit" variant="outline" data-testid="admin-streams-search-btn">
          Search
        </Button>
      </form>

      <div className="divide-y divide-zinc-200">
        {!loading && items.length === 0 && (
          <div className="p-8 text-sm text-zinc-500 text-center">
            No streams match this filter.
          </div>
        )}
        {items.map((s) => (
          <div
            key={s.id}
            className="p-4 flex items-center justify-between gap-3 flex-wrap"
            data-testid={`admin-stream-row-${s.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusPill status={s.status} />
                <Link
                  to={`/stream/${s.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-bold text-sm hover:text-[#1D78AB] truncate max-w-[40ch]"
                  data-testid={`admin-stream-title-${s.id}`}
                >
                  {s.title || s.id}
                </Link>
                {s.open_flag_count > 0 && (
                  <span className="pill text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
                    {s.open_flag_count} flag{s.open_flag_count === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-1 font-mono truncate">
                {s.seller_name || "Unknown seller"}
                <span className="mx-1.5">·</span>
                {s.viewer_count || 0} watching
                <span className="mx-1.5">·</span>
                {s.listing_count || 0} lots
                <span className="mx-1.5">·</span>
                {s.chat_message_count || 0} msgs
                {s.started_at && (
                  <>
                    <span className="mx-1.5">·</span>
                    Started {new Date(s.started_at).toLocaleString()}
                  </>
                )}
                {s.ended_by_admin && (
                  <span className="ml-2 text-red-600">· Ended by admin</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/stream/${s.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#1D78AB] underline"
                data-testid={`admin-stream-open-${s.id}`}
              >
                Open
              </Link>
              {s.status !== "ended" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => endStream(s)}
                  disabled={busy === s.id}
                  data-testid={`admin-stream-end-${s.id}`}
                >
                  <StopCircleIcon size={14} className="mr-1" />
                  {busy === s.id ? "Ending…" : "End"}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => askDelete(s)}
                disabled={busy === s.id}
                data-testid={`admin-stream-delete-${s.id}`}
              >
                <TrashIcon size={14} className="mr-1" />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="admin-stream-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">
              Permanently delete this stream?
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="text-sm text-zinc-700 leading-relaxed space-y-2">
              <p>
                <span className="font-bold">{deleteTarget.title || deleteTarget.id}</span>{" "}
                by <span className="font-mono">{deleteTarget.seller_name || "—"}</span>
              </p>
              <p className="text-xs text-zinc-500">
                This wipes all chat messages, mod records, mutes, and flags
                attached to this stream. Listings will be detached but kept so
                the seller can re-list them in a future show.
              </p>
            </div>
          )}
          <div className="mt-2">
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
              Type <span className="text-red-600 font-bold">DELETE</span> to confirm
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="mt-1"
              autoFocus
              data-testid="admin-stream-delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={!!busy}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={!!busy || confirmText.trim().toUpperCase() !== "DELETE"}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-stream-delete-confirm-submit"
            >
              {busy ? "Deleting…" : "Yes, delete it"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    live: "bg-red-600 text-white",
    scheduled: "bg-blue-600 text-white",
    ended: "bg-zinc-300 text-zinc-700",
  };
  const cls = map[status] || "bg-zinc-200 text-zinc-700";
  return (
    <span className={`pill text-[10px] uppercase tracking-wider font-bold ${cls}`}>
      {status || "unknown"}
    </span>
  );
}
